import { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link2,
  Image as ImageIcon,
  Undo,
  Redo,
} from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { mediaApi } from '../../api/mediaApi';
import { Button } from '../ui/Button';
import './RichTextEditor.css';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Éditeur de texte enrichi (HTML) pour la rédaction d'articles.
 * Contrôlé via `value` (HTML) et `onChange(html)`. L'image de couverture d'un
 * article se gère séparément (MediaPicker dédié) — cet éditeur sert uniquement
 * à insérer des images DANS le corps du texte.
 */
export function RichTextEditor({ value, onChange }) {
  const [galerieOuverte, setGalerieOuverte] = useState(false);
  const { data: medias } = useFetch(() => mediaApi.list('image'), []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({ openOnClick: false }),
      ImageExtension.configure({ inline: false }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  if (!editor) return null;

  function inserer(media) {
    editor.chain().focus().setImage({ src: `${API_URL}${media.chemin}`, alt: media.alt_text || '' }).run();
    setGalerieOuverte(false);
  }

  function ajouterLien() {
    const url = window.prompt('URL du lien :', 'https://');
    if (!url) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  return (
    <div className="rte">
      <div className="rte__toolbar">
        <button type="button" className={editor.isActive('bold') ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras">
          <Bold size={16} />
        </button>
        <button type="button" className={editor.isActive('italic') ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique">
          <Italic size={16} />
        </button>
        <button type="button" className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre H2">
          <Heading2 size={16} />
        </button>
        <button type="button" className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre H3">
          <Heading3 size={16} />
        </button>
        <button type="button" className={editor.isActive('bulletList') ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces">
          <List size={16} />
        </button>
        <button type="button" className={editor.isActive('orderedList') ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée">
          <ListOrdered size={16} />
        </button>
        <button type="button" className={editor.isActive('blockquote') ? 'is-active' : ''} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation">
          <Quote size={16} />
        </button>
        <button type="button" className={editor.isActive('link') ? 'is-active' : ''} onClick={ajouterLien} title="Lien">
          <Link2 size={16} />
        </button>
        <button type="button" onClick={() => setGalerieOuverte(true)} title="Insérer une image">
          <ImageIcon size={16} />
        </button>
        <span className="rte__divider" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} title="Annuler">
          <Undo size={16} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} title="Rétablir">
          <Redo size={16} />
        </button>
      </div>

      <EditorContent editor={editor} className="rte__content" />

      {galerieOuverte && (
        <div className="admin-image-gallery__overlay" onClick={() => setGalerieOuverte(false)}>
          <div className="admin-image-gallery" onClick={(e) => e.stopPropagation()}>
            <h3>Insérer une image</h3>
            <div className="admin-image-gallery__grid">
              {medias?.map((m) => (
                <button type="button" key={m.id} className="admin-image-gallery__item" onClick={() => inserer(m)}>
                  <img src={`${API_URL}${m.chemin}`} alt={m.alt_text || m.nom_fichier} />
                </button>
              ))}
              {!medias?.length && (
                <p>
                  Aucune image dans la médiathèque. Ajoutez-en depuis <strong>Médiathèque</strong> dans le menu.
                </p>
              )}
            </div>
            <Button type="button" variant="secondary" onClick={() => setGalerieOuverte(false)}>
              Fermer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
